/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';
import {ProvidersContext, TranslationContext} from './commons';

/**
 * Base interface for components for an entity relation
 */
export class EntityRelation<
    P extends EntityRelationProps = EntityRelationProps,
    S extends EntityRelationState = EntityRelationState
> extends React.Component<P, S> {
    public static contextType = ProvidersContext;
    public context!: React.ContextType<typeof ProvidersContext>;
    /**
     * Whether the component is currently mounted
     */
    _isMounted: boolean;
}

/**
 * The parameters for relation components
 */
export interface EntityRelationProps {
    /**
     * The entity
     */
    entity: providers.Entity;
    /**
     * The shema for the relation
     */
    relation: providers.EntityRelationSchema;
    /**
     * Whether the entity is the relation's subject
     */
    entityIsSubject: boolean;
    /**
     * Whether to enable the edition of the relations
     */
    editable: boolean;
    /**
     * The CSS class name(s) for the component in normal conditions
     */
    className: string;
    /**
     * Handler of events when the user click on a link to an entity
     */
    onClickGotoTarget: (eid: number) => void;
    /**
     * Handler fo events when the user wants to edit the relation
     */
    onClickEdit: (relation: providers.EntityRelationSchema) => void;
    /**
     * Gets the target link for the specified entity
     * @param eid The entity
     */
    getTargetLink: (eid: number) => string;
    /**
     * Gets the displayable value for the specified entity and schema
     * @param entity The entity that will be rendered
     * @param schema The schema for the entity
     */
    renderDisplayValue: (
        entity: providers.Entity,
        schema: providers.EntitySchema
    ) => JSX.Element;
    /**
     * Renders the title for the relation
     * @param component The current component (this)
     */
    renderTitle: (component: EntityRelation) => JSX.Element;
    /**
     * Renders the edit button for the relation
     * @param component The current component (this)
     */
    renderEditButton: (component: EntityRelation) => JSX.Element;
    /**
     * Renders a particular related entity
     * @param component The current component (this)
     * @param eid The EID fo the related entity
     * @param index The index in the list of related entities
     */
    renderRelationItem: (
        component: EntityRelation,
        eid: number,
        index: number
    ) => JSX.Element;
    /**
     * Renders the set of related entities
     * @param component The current component (this)
     */
    renderRelationValues: (component: EntityRelation) => JSX.Element | null;
    /**
     * Renders the component
     * @param component The current component (this)
     */
    render: (component: EntityRelation) => JSX.Element;
}

/**
 * State for the relation components
 */
export interface EntityRelationState {
    /**
     * The EID of the related entities
     */
    values: number[];
    /**
     * The displayable names of the related entities
     */
    displays: JSX.Element[];
}

/**
 * The values for the default parameters of relations components
 */
export const ENTITY_RELATION_DEFAULT_PROPS = {
    editable: false,
    className: '',
    onClickGotoTarget: (eid: number) => {
        /* do nothing */
    },
    onClickEdit: (relation: providers.EntityRelationSchema) => {
        /* do nothing */
    },
    getTargetLink: (eid: number) => `/${eid}`,
    renderDisplayValue: (
        entity: providers.Entity,
        schema: providers.EntitySchema
    ) => {
        return <span>{providers.getTitleFor(entity, schema)}</span>;
    },
    renderTitle: (component: EntityRelation) => {
        return (
            <TranslationContext.Consumer>
                {(tc) => (
                    <span>{tc.translate(component.props.relation.name)}</span>
                )}
            </TranslationContext.Consumer>
        );
    },
    renderEditButton: (component: EntityRelation) => {
        return (
            <TranslationContext.Consumer>
                {(tc) => (
                    <button
                        onClick={(event) =>
                            component.props.onClickEdit(
                                component.props.relation
                            )
                        }
                    >
                        {tc.translate('edit')}
                    </button>
                )}
            </TranslationContext.Consumer>
        );
    },
    renderRelationItem: (
        component: EntityRelation,
        eid: number,
        index: number
    ) => {
        if (component.state.displays.length === 0) {
            return <span>{eid.toString()}</span>;
        }
        return component.state.displays[index];
    },
    renderRelationValues: (component: EntityRelation) => {
        return (
            <span>
                {component.state.values.map((eid: number, index: number) => (
                    <span
                        key={`${
                            component.props.entity.eid
                        }_${providers.getUniqueRelationName(
                            component.props.relation
                        )}_${index}`}
                    >
                        {index > 0 ? ', ' : ''}
                        <a
                            href={component.props.getTargetLink(eid)}
                            onClick={(event) => {
                                event.preventDefault();
                                component.props.onClickGotoTarget(eid);
                            }}
                        >
                            {component.props.renderRelationItem(
                                component,
                                eid,
                                index
                            )}
                        </a>
                    </span>
                ))}
            </span>
        );
    },
    render: (component: EntityRelation) => {
        return (
            <tr className={component.props.className}>
                <td>{component.props.renderTitle(component)}</td>
                <td>
                    {component.props.renderRelationValues(component)}{' '}
                    {component.props.editable === true
                        ? component.props.renderEditButton(component)
                        : null}
                </td>
            </tr>
        );
    },
};
